import { useEffect, useState } from 'react';
import { Plus, Trash2, RefreshCw, Users as UsersIcon, Shield, ShieldOff, Edit, Mail, DollarSign, Network, Eye, ShoppingCart, Search, ShieldAlert, Globe, CalendarClock } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../components/ui/Toast';
import { UserAccount, UserType, ApiResponse } from '../types';
import { format } from 'date-fns';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/ConfirmDialog';

const typeConfig: { key: UserType; label: string; desc: string; color: string }[] = [
  { key: 'read', label: 'Read', desc: 'View domains, tags, and reports', color: 'bg-slate-100 text-slate-700' },
  { key: 'outreach', label: 'Outreach', desc: 'Read-only, plus edit cost, currency, and acquisition method', color: 'bg-teal-100 text-teal-700' },
  { key: 'classify', label: 'Classify', desc: 'Edit classification fields, append to notes, create and apply tags', color: 'bg-amber-100 text-amber-700' },
  { key: 'admin', label: 'Admin', desc: 'Manage domains, registrars, tags, WHOIS, users, API keys', color: 'bg-purple-100 text-purple-700' },
  { key: 'super_admin', label: 'Super Admin', desc: 'Overview of all API keys across all users', color: 'bg-red-100 text-red-700' },
];

function typeBadgeColor(type: UserType): string {
  return typeConfig.find(t => t.key === type)?.color || 'bg-slate-100 text-slate-700';
}

function typeLabel(type: UserType): string {
  return typeConfig.find(t => t.key === type)?.label || type;
}

export default function UsersPage() {
  const { fetchApi, user: currentUser } = useApi();
  const toast = useToast();
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const confirm = useConfirm();
  const [editingUser, setEditingUser] = useState<UserAccount | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const res = await fetchApi<ApiResponse<UserAccount[]>>('/users');
      if (res.data) setUsers(res.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async (user: UserAccount) => {
    if (!(await confirm({
      title: 'Delete user?',
      message: `Are you sure you want to delete user "${user.username}"?`,
      tone: 'danger',
    }))) return;

    try {
      await fetchApi(`/users/${user.id}`, { method: 'DELETE' });
      loadUsers();
    } catch (error) {
      toast.error('Failed to delete user', error instanceof Error ? error.message : undefined);
    }
  };

  const handleToggleActive = async (user: UserAccount) => {
    const newActive = !user.is_active;
    const action = newActive ? 'activate' : 'deactivate';
    if (!(await confirm({
      title: newActive ? 'Activate user?' : 'Deactivate user?',
      message: `Are you sure you want to ${action} user "${user.username}"?`,
      confirmLabel: newActive ? 'Activate' : 'Deactivate',
      tone: newActive ? 'default' : 'danger',
    }))) return;

    try {
      await fetchApi(`/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: newActive }),
      });
      loadUsers();
    } catch (error) {
      toast.error(`Failed to ${action} user`, error instanceof Error ? error.message : undefined);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        actions={
          <button
            onClick={() => { setEditingUser(null); setShowModal(true); }}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        }
      />

      {loading ? (
        <div className="card overflow-hidden">
          <table className="w-full"><tbody><SkeletonRows rows={5} cols={8} /></tbody></table>
        </div>
      ) : users.length === 0 ? (
        <div className="card">
          <EmptyState icon={UsersIcon} title="No users found" />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="table-header">Username</th>
                <th className="table-header">Type</th>
                <th className="table-header">Domain Scope</th>
                <th className="table-header">Access</th>
                <th className="table-header">Status</th>
                <th className="table-header">Last Login</th>
                <th className="table-header">Created</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full shrink-0" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          <span className="text-xs font-medium text-slate-500">{u.username.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate-900">
                          {u.username}
                          {currentUser?.id === u.id && (
                            <span className="ml-2 text-xs text-brand-600 font-normal">(you)</span>
                          )}
                        </div>
                        {u.email && (
                          <div className="text-xs text-slate-500">{u.email}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeBadgeColor(u.type)}`}
                    >
                      {typeLabel(u.type)}
                    </span>
                  </td>
                  <td className="table-cell text-sm text-slate-500">
                    {u.allowed_domain_types === null || u.allowed_domain_types === undefined
                      ? <span className="text-amber-600 font-medium">Unrestricted</span>
                      : u.allowed_domain_types.length === 0
                        ? <span className="text-slate-400 italic">No access</span>
                        : <span className="font-mono">{u.allowed_domain_types.join(', ')}</span>
                    }
                  </td>
                  <td className="table-cell text-sm">
                    <div className="flex flex-wrap gap-1">
                      {u.show_cost && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          <DollarSign className="h-3 w-3" />Cost
                        </span>
                      )}
                      {u.show_mind_map && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                          <Network className="h-3 w-3" />Map
                        </span>
                      )}
                      {u.show_acquisition_method && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          <ShoppingCart className="h-3 w-3" />Acq
                        </span>
                      )}
                      {u.show_whois && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200">
                          <Search className="h-3 w-3" />WHOIS
                        </span>
                      )}
                      {u.show_footprint_audit && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
                          <ShieldAlert className="h-3 w-3" />Footprint
                        </span>
                      )}
                      {u.can_trigger_sync && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <RefreshCw className="h-3 w-3" />Sync
                        </span>
                      )}
                      {u.can_manage_nameservers && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                          <Globe className="h-3 w-3" />NS
                        </span>
                      )}
                      {u.can_renew_domains && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
                          <CalendarClock className="h-3 w-3" />Renew
                        </span>
                      )}
                      {!u.show_cost && !u.show_mind_map && !u.show_acquisition_method && !u.show_whois && !u.show_footprint_audit && !u.can_trigger_sync && !u.can_manage_nameservers && !u.can_renew_domains && (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <Shield className="h-3 w-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                        <ShieldOff className="h-3 w-3" /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="table-cell text-slate-500 text-sm">
                    {u.last_login_at
                      ? format(new Date(u.last_login_at), 'MMM d, yyyy h:mm a')
                      : 'Never'}
                  </td>
                  <td className="table-cell text-slate-500 text-sm">
                    {format(new Date(u.created_at), 'MMM d, yyyy')}
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => { setEditingUser(u); setShowModal(true); }}
                        className="p-1.5 text-slate-400 hover:text-brand-600"
                        title="Edit user"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {currentUser?.id !== u.id && (
                        <>
                          <button
                            onClick={() => handleToggleActive(u)}
                            className="p-1.5 text-slate-400 hover:text-yellow-600"
                            title={u.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {u.is_active ? (
                              <ShieldOff className="h-4 w-4" />
                            ) : (
                              <Shield className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(u)}
                            className="p-1.5 text-slate-400 hover:text-red-600"
                            title="Delete user"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <UserModal
          user={editingUser}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadUsers();
          }}
        />
      )}
    </div>
  );
}

function UserModal({
  user,
  onClose,
  onSuccess,
}: {
  user: UserAccount | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { fetchApi, user: currentUser, isSuperAdmin } = useApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [type, setType] = useState<UserType>(user?.type || 'classify');
  // Per-user domain_type scope. null = unrestricted; array = restricted.
  const [scopeUnrestricted, setScopeUnrestricted] = useState<boolean>(user?.allowed_domain_types == null);
  const [scopeTypes, setScopeTypes] = useState<string[]>(user?.allowed_domain_types || []);
  const [showCost, setShowCost] = useState<boolean>(user?.show_cost ?? false);
  const [showMindMap, setShowMindMap] = useState<boolean>(user?.show_mind_map ?? false);
  const [showAcquisition, setShowAcquisition] = useState<boolean>(user?.show_acquisition_method ?? false);
  const [showWhois, setShowWhois] = useState<boolean>(user?.show_whois ?? false);
  const [showFootprintAudit, setShowFootprintAudit] = useState<boolean>(user?.show_footprint_audit ?? false);
  const [canTriggerSync, setCanTriggerSync] = useState<boolean>(user?.can_trigger_sync ?? false);
  const [canManageNameservers, setCanManageNameservers] = useState<boolean>(user?.can_manage_nameservers ?? false);
  const [canRenewDomains, setCanRenewDomains] = useState<boolean>(user?.can_renew_domains ?? false);
  const [availableDomainTypes, setAvailableDomainTypes] = useState<string[]>([]);
  const isSelf = !!user && currentUser?.id === user.id;

  // Selecting the Outreach role pre-fills its standard grants (admin can still
  // adjust before saving): scope to o&o, and cost + acquisition visibility.
  const handleTypeChange = (next: UserType) => {
    setType(next);
    if (next === 'outreach') {
      setScopeUnrestricted(false);
      setScopeTypes((prev) => (prev.includes('o&o') ? prev : [...prev, 'o&o']));
      setShowCost(true);
      setShowAcquisition(true);
    }
  };

  // Load the live list of domain types (same source the filters use). New
  // types added by classifying domains appear here automatically.
  useEffect(() => {
    fetchApi<ApiResponse<Record<string, string[]>>>('/reports/filter-options')
      .then(res => {
        if (res.data?.domain_type) setAvailableDomainTypes(res.data.domain_type);
      })
      .catch(() => { /* not critical */ });
  }, [fetchApi]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (user) {
        const body: Record<string, unknown> = { type, email: email || undefined };
        if (password) body.password = password;
        // Editing self: API rejects scope changes on own account, so omit.
        if (!isSelf) {
          body.allowed_domain_types = scopeUnrestricted ? null : scopeTypes;
          body.show_cost = showCost;
          body.show_mind_map = showMindMap;
          body.show_whois = showWhois;
          body.show_footprint_audit = showFootprintAudit;
          body.can_trigger_sync = canTriggerSync;
          body.can_manage_nameservers = canManageNameservers;
          // Acquisition-method visibility is a super_admin-only grant.
          if (isSuperAdmin) body.show_acquisition_method = showAcquisition;
          // Renewal access is a super_admin-only grant.
          if (isSuperAdmin) body.can_renew_domains = canRenewDomains;
        }
        await fetchApi(`/users/${user.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      } else {
        await fetchApi('/users', {
          method: 'POST',
          body: JSON.stringify({
            username,
            password: password || undefined,
            email: email || undefined,
            type,
            allowed_domain_types: scopeUnrestricted ? null : scopeTypes,
            show_cost: showCost,
            show_mind_map: showMindMap,
            show_whois: showWhois,
            show_footprint_audit: showFootprintAudit,
            can_trigger_sync: canTriggerSync,
            can_manage_nameservers: canManageNameservers,
            ...(isSuperAdmin ? { show_acquisition_method: showAcquisition, can_renew_domains: canRenewDomains } : {}),
          }),
        });
      }
      onSuccess();
    } catch (error) {
      toast.error(`Failed to ${user ? 'update' : 'create'} user`, error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const availableTypes = isSuperAdmin
    ? typeConfig
    : typeConfig.filter(t => t.key !== 'super_admin');

  return (
    <Modal
      title={user ? 'Edit User' : 'Create User'}
      onClose={onClose}
      size="lg"
      dismissible={!loading}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="user-form" disabled={loading} className="btn btn-primary">
            {loading ? 'Saving...' : user ? 'Update User' : 'Create User'}
          </button>
        </>
      }
    >
        <form id="user-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="label">Username *</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="input"
                required
                disabled={!!user}
                minLength={3}
              />
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email {!user && '(required for Google sign-in)'}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@company.com"
                  className="input pl-10"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Users with a Google email can sign in with Google
              </p>
            </div>

            <div>
              <label htmlFor="password" className="label">
                {user ? 'New Password (leave blank to keep current)' : 'Password'}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={user ? 'Leave blank to keep current' : 'Minimum 8 characters (optional if email set)'}
                className="input"
                required={!user && !email}
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="label">Role</label>
              <div className="space-y-2">
                {availableTypes.map((t) => (
                  <label
                    key={t.key}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50 ${
                      type === t.key ? 'border-brand-500 bg-brand-50' : 'border-slate-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="userType"
                      checked={type === t.key}
                      onChange={() => handleTypeChange(t.key)}
                      className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-900">{t.label}</div>
                      <div className="text-xs text-slate-500">{t.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Domain-type scope. Only meaningful for non-super_admin users.
                The API rejects scope changes on the user's own account, so
                the field is disabled in that case. */}
            {type !== 'super_admin' && (
              <div>
                <label className="label">Domain-type scope</label>
                <p className="text-xs text-slate-500 mb-2">
                  Restrict this user to only see domains of certain types.{' '}
                  {isSelf && <span className="text-amber-600">You cannot change your own scope.</span>}
                </p>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={scopeUnrestricted}
                    disabled={isSelf}
                    onChange={(e) => setScopeUnrestricted(e.target.checked)}
                    className="h-4 w-4 text-brand-600 border-slate-300 rounded"
                  />
                  <span className="text-sm">Unrestricted (sees all domains)</span>
                </label>
                {!scopeUnrestricted && (
                  <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                    {availableDomainTypes.length === 0 ? (
                      <p className="text-sm text-slate-500 italic">No classified domain types found yet. Classify some domains first to populate this list.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {availableDomainTypes.map((t) => (
                          <label key={t} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={scopeTypes.includes(t)}
                              disabled={isSelf}
                              onChange={(e) => {
                                if (e.target.checked) setScopeTypes([...scopeTypes, t]);
                                else setScopeTypes(scopeTypes.filter(s => s !== t));
                              }}
                              className="h-4 w-4 text-brand-600 border-slate-300 rounded"
                            />
                            <span className="text-sm">{t}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {scopeTypes.length === 0 && availableDomainTypes.length > 0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        No types selected — this user will see no domains until you select at least one or switch to Unrestricted.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Eye className="h-4 w-4 text-slate-400" />
                <label className="label mb-0">Access & Visibility</label>
              </div>
              {isSelf && (
                <p className="text-xs text-amber-600 mb-2">You cannot change your own access settings.</p>
              )}
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                <label className={`flex items-start gap-3 p-3 ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                  <input
                    type="checkbox"
                    checked={showCost}
                    disabled={isSelf}
                    onChange={(e) => setShowCost(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm font-medium text-slate-800">Cost & Currency</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Can view acquisition cost and currency on domains</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                  <input
                    type="checkbox"
                    checked={showMindMap}
                    disabled={isSelf}
                    onChange={(e) => setShowMindMap(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Network className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm font-medium text-slate-800">Mind Map</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Can access the WHOIS contact mind map (sensitive)</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                  <input
                    type="checkbox"
                    checked={showWhois}
                    disabled={isSelf}
                    onChange={(e) => setShowWhois(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Search className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm font-medium text-slate-800">WHOIS</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Can access the WHOIS search and lookup feature (view-only; lookups stay admin-only)</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                  <input
                    type="checkbox"
                    checked={showFootprintAudit}
                    disabled={isSelf}
                    onChange={(e) => setShowFootprintAudit(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <ShieldAlert className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm font-medium text-slate-800">Footprint Audit</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Can view footprint audit results (running an audit stays admin-only)</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                  <input
                    type="checkbox"
                    checked={canTriggerSync}
                    disabled={isSelf}
                    onChange={(e) => setCanTriggerSync(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm font-medium text-slate-800">Trigger Sync</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Can refresh registrar accounts on demand (no access to credentials or registrar settings)</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                  <input
                    type="checkbox"
                    checked={canManageNameservers}
                    disabled={isSelf}
                    onChange={(e) => setCanManageNameservers(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm font-medium text-slate-800">Manage Nameservers</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Can change nameservers at the registrar (live DNS change) for domains within their scope</p>
                  </div>
                </label>
                {isSuperAdmin && (
                  <label className={`flex items-start gap-3 p-3 ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                    <input
                      type="checkbox"
                      checked={canRenewDomains}
                      disabled={isSelf}
                      onChange={(e) => setCanRenewDomains(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                    />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-sm font-medium text-slate-800">Renew Domains</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">Can renew domains within their scope — <strong>charges the registrar account</strong>. Super admins only can grant this.</p>
                    </div>
                  </label>
                )}
                {isSuperAdmin && (
                  <label className={`flex items-start gap-3 p-3 ${isSelf ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                    <input
                      type="checkbox"
                      checked={showAcquisition}
                      disabled={isSelf}
                      onChange={(e) => setShowAcquisition(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                    />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <ShoppingCart className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-sm font-medium text-slate-800">Acquisition Method</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">Can view how each domain was acquired (super admin grant)</p>
                    </div>
                  </label>
                )}
              </div>
            </div>
        </form>
    </Modal>
  );
}
